
import React, { useRef, useState, useEffect } from "react";
import HTMLFlipBook from "react-pageflip";
import { FaArrowLeft, FaArrowRight, FaDownload } from "react-icons/fa";
import emailjs from "@emailjs/browser";
import "../css/FlipBook.css";

const FlipBook = ({ isOpen, setIsOpen, pages, brochureUrl }) => {
  const bookRef = useRef(null);
  const [page, setPage] = useState(1);
  const [showPopupForm, setShowPopupForm] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [bookSize, setBookSize] = useState({ width: 1100, height: 800 });

  useEffect(() => {
    const checkWindowSize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      const containerWidth = Math.min(window.innerWidth * 0.9, 1100);
      const containerHeight = Math.min(window.innerHeight * 0.8, 800);
      setBookSize(mobile ? 
        { width: containerWidth * 0.95, height: containerHeight * 0.8 } : 
        { width: containerWidth, height: containerHeight }
      );
    };

    checkWindowSize();
    window.addEventListener("resize", checkWindowSize);
    return () => window.removeEventListener("resize", checkWindowSize);
  }, []);

  const onPageChange = (e) => {
    setPage(e.data + 1);
  };

  const handleClose = () => {
    setIsOpen(false);
    setShowPopupForm(false);
    setPage(1);
  };

  return (
    <div className="flipbook-container">
      <button 
        className="open-button" 
        onClick={() => setIsOpen(true)}
        aria-label="Open Flipbook"
      >
        Open Flipbook
      </button>

      {isOpen && (
        <div className="popup-overlay" onClick={handleClose}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-button" onClick={handleClose} aria-label="Close Flipbook">✕</button>

            <div className="flipbook-header">
              <button
                className="download-button"
                onClick={() => setShowPopupForm(true)}
                aria-label="Download Brochure"
              >
                <FaDownload size={20} /> Download Brochure
              </button>
              <span className="page-counter">Page {page} / {pages.length}</span>
            </div>

            {showPopupForm && <DownloadForm setShowPopupForm={setShowPopupForm} brochureUrl={brochureUrl} />}

            <div className={`flipbook-wrapper ${isMobile ? "mobile" : ""}`}>
              <button
                className="nav-button left"
                onClick={() => bookRef.current.pageFlip().flipPrev()}
                aria-label="Previous Page"
                disabled={page === 1}
              >
                <FaArrowLeft size={24} />
              </button>

              <HTMLFlipBook
                ref={bookRef}
                width={bookSize.width}
                height={bookSize.height}
                showCover={true}
                flippingTime={800}
                onFlip={onPageChange}
                className="flipbook"
              >
                {pages.map((imgSrc, index) => (
                  <div className="page" key={index}>
                    <img src={imgSrc} alt={`Brochure Page ${index + 1}`} loading="lazy" />
                  </div>
                ))}
              </HTMLFlipBook>

              <button
                className="nav-button right"
                onClick={() => bookRef.current.pageFlip().flipNext()}
                aria-label="Next Page"
                disabled={page === pages.length}
              >
                <FaArrowRight size={24} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Download Form Component with EmailJS
const DownloadForm = ({ setShowPopupForm, brochureUrl }) => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    if (!formData.name || !formData.email || !formData.phone) {
      setError("All fields are required.");
      setIsLoading(false);
      return;
    }

    try {
      // EmailJS template params
      const templateParams = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: "Popup Brochure Download Request",
        page_url: window.location.href, // Capture the current page URL
      };

      // Send Email using EmailJS
      await emailjs.send(
        "service_i2h82eb",   // Replace with your EmailJS service ID
        "template_rn1vnln",  // Replace with your EmailJS template ID
        templateParams,
        "hjLXq5MC66R977QFn"       // Replace with your EmailJS user ID
      );

      // Proceed with the file download
      const link = document.createElement("a");
      link.href = brochureUrl;
      link.download = "Brochure.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setShowPopupForm(false);
      setFormData({ name: "", email: "", phone: "" });
    } catch (error) {
      setError("Failed to send email. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="form-popup-overlay" onClick={() => setShowPopupForm(false)}>
      <div className="popup-content1" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={() => setShowPopupForm(false)} aria-label="Close Download Form">✕</button>
        <form className="brochure-form" onSubmit={handleDownload}>
          <h3>Download Brochure</h3>
          {error && <div className="error-message">{error}</div>}
          <input type="text" name="name" placeholder="Your Name" value={formData.name} onChange={handleInputChange} required />
          <input type="email" name="email" placeholder="Your Email" value={formData.email} onChange={handleInputChange} required />
          <input type="tel" name="phone" placeholder="Your Phone Number" value={formData.phone} onChange={handleInputChange} required />
          <button type="submit" disabled={isLoading}>{isLoading ? "Sending..." : "Submit & Download"}</button>
        </form>
      </div>
    </div>
  );
};

export default FlipBook;
